import { useDrop } from 'react-dnd';
import DeleteIcon from '@mui/icons-material/Delete';
import { useFormContext, useFieldArray, useWatch, Controller } from 'react-hook-form';
import {
  Card,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import { useEffect } from 'react';
import { Service } from '../../../../../models/masters/service';
import { FormCurrencyInput } from '../../../../../components/hook-form/form-currency-input.component';
import { FormInput } from '../../../../../components/hook-form/form-input.component';
import { FormCheckbox } from '../../../../../components/hook-form/form-checkbox.component';
import { GuardedComponent } from '../../../../../components/guarded-component';
import { UserPermissionName } from '../../../../../models/user-permission-name';

interface BundleItemDropableProps {
  propName: string;
  onDrop: (item: Service) => void;
  onRemoveSplit: () => void;
  onRemoveService: (item: Service) => void;
}

type GroupedServices = {
  [key: string]: Service[]; // Index signature for numerical keys
};

export const BundleItemDropable = ({ propName, onDrop, onRemoveSplit, onRemoveService }: BundleItemDropableProps) => {
  const { control, setValue, getValues } = useFormContext();

  const { fields, append, remove } = useFieldArray({
    control,
    name: `${propName}.serviceBundleSplitItems`,
    keyName: 'sid',
  });

  useWatch({
    name: ['serviceBundleSplitUps', 'bundlePrice', 'serviceBundleSplitItems'] || [],
  });

  const formatAccrualAmount = (amount: number): string => {
    // Convert amount to string to handle formatting
    const amountString = amount.toString();

    // Check if the amount has a decimal point
    const decimalIndex = amountString?.indexOf('.');

    if (decimalIndex !== -1) {
      // If there's a decimal point, format to show only two decimal places
      const integerPart = amountString.slice(0, decimalIndex);
      const decimalPart = amountString.slice(decimalIndex + 1, decimalIndex + 3);
      return `${integerPart}.${decimalPart}`;
    } else {
      // If there's no decimal point, return the amount as is
      return amountString;
    }
  };

  const handleSplitAmount = () => {
    const amount = getValues(`${propName}.splitAmt`);
    if (getValues(`${propName}.serviceBundleSplitItems`)?.length > 0) {
      let splitAmount = 0;
      splitAmount = amount / getValues(`${propName}.serviceBundleSplitItems`)?.length;
      let formattedSplitAmount = formatAccrualAmount(splitAmount);
      if (!formattedSplitAmount.includes('.')) {
        formattedSplitAmount += '.00';
      }
      setValue(`${propName}.accuralAmount`, formattedSplitAmount);
    }
  };

  useEffect(() => {
    handleSplitAmount();
    if (getValues('serviceBundleSplitUps')?.length > 0) {
      const totalSplitAmt = getValues('serviceBundleSplitUps')?.reduce((total: any, item: any) => {
        const splitAmtNumber = !item.splitAmt ? 0 : parseFloat(item.splitAmt); // Convert 'splitAmt' to a number
        return total + splitAmtNumber; // Add to the running total
      }, 0);

      const formattedTotalSplitAmt = totalSplitAmt?.toFixed(2);

      setValue('bundlePrice', formattedTotalSplitAmt);
    }
  }, [getValues(`${propName}.splitEvenly`), getValues(`${propName}.splitAmt`)]);

  const handleItemDropDefaultvalueSet = () => {
    handleSplitAmount();
  };

  // const [{ isOver }, drop] = useDrop({
  //   accept: 'service',
  //   drop: (item: any) => {
  //     append(item.service);
  //     onDrop(item.service);

  //     handleItemDropDefaultvalueSet();
  //   },
  //   collect: (monitor) => ({
  //     isOver: monitor.isOver(),
  //   }),
  // });

  const [{ isOver }, drop] = useDrop({
    accept: ['service', 'serviceGroup'], // Accept both individual services and entire service groups
    drop: (item: any) => {
      if (item.type === 'service') {
        append(item.service);
        onDrop(item.service);
        handleItemDropDefaultvalueSet();
      } else if (item.type === 'serviceGroup') {
        // Handle dropping of entire service group
        Array.isArray(item?.serviceGroup?.services) &&
          item?.serviceGroup?.services?.forEach(async (service: Service) => {
            append(service);
            onDrop(service);
            handleItemDropDefaultvalueSet();
          });
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  });

  const handleDeleteService = (service: Service, index: number) => {
    // API call for edit
    const currentIndex = fields?.findIndex((serviceValue: any) => serviceValue?.serviceID === service?.serviceID);

    remove(currentIndex);
    onRemoveService(service);
    handleSplitAmount();
  };

  const handleDeleteSplit = () => {
    // API call for edit
    onRemoveSplit();
  };

  const groupServices = (services: Service[]): GroupedServices => {
    return services.reduce((acc, service) => {
      const { serviceGroupName } = service;

      if (!acc[serviceGroupName]) {
        acc[serviceGroupName] = []; // Initialize array if not already present
      }

      acc[serviceGroupName].push(service); // Add service to the correct group
      return acc;
    }, {} as GroupedServices);
  };

  const groupedServices = groupServices(getValues(`${propName}.serviceBundleSplitItems`));

  const GroupedServiceList = ({ groupedServices }: { groupedServices: GroupedServices }) => {
    return (
      <div style={{ padding: '15px 0', margin: '0' }}>
        {Object.entries(groupedServices).map(([groupName, services], index) => {
          return (
            <div className="bundleGroupContainer" key={groupName}>
              <Typography variant="h6">{groupName}</Typography> {/* Display group name */}
              <List>
                {services.map((service, index) => (
                  <ListItem key={service.serviceID}>
                    <ListItemText
                      primary={
                        <Grid
                          container
                          spacing={2}
                          justifyContent="space-between"
                          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                        >
                          <Grid item>{service.serviceName}</Grid>
                          <Grid item style={{ marginRight: 20 }}>
                            {!getValues(`${propName}.splitEvenly`)
                              ? ''
                              : getValues(`${propName}.accuralAmount`) !== '0.00' &&
                              getValues(`${propName}.accuralAmount`) &&
                              `$${getValues(`${propName}.accuralAmount`)}`}
                          </Grid>
                        </Grid>
                      }
                    />
                    <IconButton onClick={() => handleDeleteService(service, index)}>
                      <Tooltip title="Delete Service">
                        <DeleteIcon className="btnDelete" />
                      </Tooltip>
                    </IconButton>
                  </ListItem>
                ))}
              </List>
              {/* To separate groups visually */}
            </div>
          );
        })}
        <div className="dropHereBlock">
          <span className="textwarning">Drag services from the right side and drop them here.</span>
        </div>
      </div>
    );
  };

  return (
    <div
      ref={drop}
      style={{
        backgroundColor: isOver ? 'lightgreen' : 'white',
        width: '100%',
        marginBottom: '10px',
        border: '1px solid #f1f1f1',
        borderRadius: '10px',
      }}
    >
      <Card style={{ padding: '15px' }} className="slplitBlock">
        <div style={{ display: 'flex', gap: '10px' }}>
          {/* <span style={{ minWidth: '85px', display: 'flex', alignItems: 'center' }}>Split Bundle</span> */}
          <FormInput name={`${propName}.splitName`} label="Split name" />
          {/* <FormInput name={`${propName}.splitAmt`} label=" " /> */}
          <FormCurrencyInput label="Split Amount   " name={`${propName}.splitAmt`} />
          <GuardedComponent requiredPermissions={[UserPermissionName.DELETE_SERVICE_BUNDLE]}>
            <IconButton aria-label="delete" onClick={handleDeleteSplit}>
              <Tooltip title={<Typography variant="body1">Delete</Typography>}>
                <DeleteIcon className="btnDelete" />
              </Tooltip>
            </IconButton>
          </GuardedComponent>
          <FormCheckbox label="Split Evenly" name={`${propName}.splitEvenly`} />
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '16px' }}>
          <strong style={{ width: '50%' }}>Services</strong>
          {!!getValues(`${propName}.splitEvenly`) && <strong style={{ width: '150px' }}>Accrual</strong>}
        </div>
        <GroupedServiceList groupedServices={groupedServices} />
      </Card>
    </div>
  );
};
